import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { DynamicTooltip } from 'shared/ui/Tooltip';

import type { SxProps, Theme } from '@mui/material/styles';
import type { KeyboardEvent, ReactNode } from 'react';

interface ReportMetricProps {
    title: string;
    icon?: ReactNode;

    primary: ReactNode | string;
    primaryColor?: string;
    primarySx?: SxProps<Theme>;
    primaryTooltip?: string | JSX.Element;

    secondary?: ReactNode | string;
    secondarySx?: SxProps<Theme>;

    children?: ReactNode;
    loading?: boolean;
    sx?: SxProps<Theme>;
    minHeight?: number | string;
    onClick?: () => void;
}

export const ReportMetric = ({
    title,
    icon,
    primary,
    primaryColor,
    primarySx,
    primaryTooltip,
    secondary,
    secondarySx,
    children,
    loading = false,
    sx,
    minHeight = '200px',
    onClick,
}: ReportMetricProps) => {
    const { t } = useTranslation();

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!onClick) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
        }
    };

    return (
        <Box
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            sx={{
                flex: '1 1 0',
                minWidth: 160,
                border: '1px solid #E0E0E0',
                borderRadius: '12px',
                padding: 2,
                backgroundColor: '#FFFFFF',
                minHeight,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'background-color 0.15s ease-out',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': onClick
                    ? {
                          backgroundColor: 'action.hover',
                      }
                    : undefined,
                '&:hover .report-metric-see-details': onClick
                    ? {
                          color: 'primary.dark',
                      }
                    : undefined,
                ...sx,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    marginBottom: '6px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        height: '20px',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'rgba(0, 0, 0, 0.50)',
                            fontSize: '13px',
                            lineHeight: '20px',
                            fontWeight: 600,
                        }}
                    >
                        {title}
                    </Typography>
                    {primaryTooltip && (
                        <DynamicTooltip
                            content={primaryTooltip}
                            containerStyle={{ paddingTop: '6px' }}
                        >
                            <InfoOutlinedIcon
                                sx={{
                                    fontSize: 18,
                                    color: '#B7B7B7',
                                }}
                                data-testclass="icon-tooltip"
                            />
                        </DynamicTooltip>
                    )}
                </Box>
                {icon && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: primaryColor ?? 'text.secondary',
                        }}
                    >
                        {icon}
                    </Box>
                )}
            </Box>

            {loading ? (
                <Skeleton
                    variant="text"
                    sx={{
                        fontSize: '38px',
                        lineHeight: '52px',
                        fontWeight: 700,
                        width: '60%',
                        height: '52px',
                    }}
                />
            ) : (
                <Typography
                    data-testclass="report-metric-primary"
                    sx={{
                        fontSize: '38px',
                        lineHeight: '52px',
                        fontWeight: 700,
                        color: primaryColor ?? 'common.black',
                        ...primarySx,
                    }}
                >
                    {primary}
                </Typography>
            )}

            {!loading && secondary && (
                <Typography
                    variant="body2"
                    data-testclass="report-metric-secondary"
                    sx={{
                        color: 'rgba(0, 0, 0, 0.50)',
                        fontSize: '13px',
                        lineHeight: '20px',
                        ...secondarySx,
                    }}
                >
                    {secondary}
                </Typography>
            )}

            {!loading && children && (
                <Box
                    sx={{
                        marginTop: '16px',
                        paddingTop: '24px',
                        borderTop: '1px solid #E0E0E0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                    }}
                >
                    {children}
                </Box>
            )}

            {!loading && onClick && (
                <Typography
                    variant="body2"
                    data-testclass="report-metric-see-details"
                    sx={{
                        marginTop: 'auto',
                        fontSize: '13px',
                        lineHeight: '16px',
                        fontWeight: 400,
                        color: 'primary.main',
                    }}
                >
                    {t('reportMetric.seeDetails', 'See details')}
                </Typography>
            )}
        </Box>
    );
};
